import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

/**
 * Get compensation claims with filtering and pagination
 * @param {Object} filter - MongoDB filter object
 * @param {number} page - Page number
 * @param {number} pageLimit - Items per page
 * @param {string} projection - Field projection
 * @param {Object} sort - Sort criteria
 * @returns {Promise} Compensation claims data
 */
export const getCompensationClaims = (filter = {}, page = 1, pageLimit = 10, projection = '', sort = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.READ,
          {
            filter: JSON.stringify(filter),
            page,
            pageLimit,
            projection,
            sort: JSON.stringify(sort)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get single compensation claim by ID
 * @param {string} claimId - Claim ID
 * @returns {Promise} Claim data
 */
export const getCompensationClaimById = (claimId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.FIND_BY_ID,
          {
            recordId: claimId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Create new compensation claim
 * @param {Object} data - Claim data
 * @returns {Promise} Created claim
 */
export const createCompensationClaim = (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Generate claim number based on type and date
        const claimType = data.claimType || 'COID'
        const year = new Date().getFullYear()
        const count = await countCompensationClaims({
          claimType,
          createdAt: {
            $gte: new Date(`${year}-01-01`),
            $lte: new Date(`${year}-12-31`)
          }
        })

        data = {
          ...data,
          claimNumber: `${claimType}-${year}-${String(count + 1).padStart(4, '0')}`,
          entityRef: store.getState().core.entity._id,
          createdBy: store.getState().auth.agiliteUser?._id,
          createdAt: new Date().toISOString(),
          status: BillingEnums.claimStatus.DRAFT
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.CREATE,
          {
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update compensation claim
 * @param {string} claimId - Claim ID
 * @param {Object} data - Updated data
 * @returns {Promise} Updated claim
 */
export const updateCompensationClaim = (claimId, data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        data = {
          ...data,
          modifiedBy: store.getState().auth.agiliteUser?._id,
          modifiedAt: new Date().toISOString()
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.UPDATE,
          {
            recordId: claimId,
            data: JSON.stringify(data)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Count compensation claims
 * @param {Object} filter - Filter criteria
 * @returns {Promise} Count
 */
export const countCompensationClaims = (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        filter = {
          ...filter,
          entityRef: store.getState().core.entity._id
        }

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.COUNT,
          {
            filter: JSON.stringify(filter)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Aggregate compensation claims data
 * @param {Array} pipeline - MongoDB aggregation pipeline
 * @returns {Promise} Aggregation result
 */
export const aggregateCompensationClaims = (pipeline = []) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Add entity filter as first stage
        pipeline.unshift({
          $match: {
            entityRef: store.getState().core.entity._id
          }
        })

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Update claim status with validation
 * @param {string} claimId - Claim ID
 * @param {string} newStatus - New status
 * @param {string} notes - Status change notes
 * @returns {Promise} Updated claim
 */
export const updateClaimStatus = (claimId, newStatus, notes = '') => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get current claim
        const currentClaim = await getCompensationClaimById(claimId)

        if (!currentClaim) {
          throw new Error(BillingEnums.errorMessages.CLAIM_NOT_FOUND)
        }

        // Validate status transition
        const validTransitions = {
          [BillingEnums.claimStatus.DRAFT]: [BillingEnums.claimStatus.SUBMITTED],
          [BillingEnums.claimStatus.SUBMITTED]: [
            BillingEnums.claimStatus.UNDER_REVIEW,
            BillingEnums.claimStatus.REJECTED
          ],
          [BillingEnums.claimStatus.UNDER_REVIEW]: [
            BillingEnums.claimStatus.ADDITIONAL_INFO_REQUIRED,
            BillingEnums.claimStatus.APPROVED,
            BillingEnums.claimStatus.PARTIALLY_APPROVED,
            BillingEnums.claimStatus.REJECTED
          ],
          [BillingEnums.claimStatus.ADDITIONAL_INFO_REQUIRED]: [
            BillingEnums.claimStatus.UNDER_REVIEW,
            BillingEnums.claimStatus.REJECTED
          ],
          [BillingEnums.claimStatus.APPROVED]: [BillingEnums.claimStatus.PAID],
          [BillingEnums.claimStatus.PARTIALLY_APPROVED]: [BillingEnums.claimStatus.PAID],
          [BillingEnums.claimStatus.PAID]: [BillingEnums.claimStatus.CLOSED]
        }

        const allowedTransitions = validTransitions[currentClaim.status] || []
        if (!allowedTransitions.includes(newStatus)) {
          throw new Error(BillingEnums.errorMessages.INVALID_STATUS)
        }

        // Update status with audit trail
        const updateData = {
          status: newStatus,
          statusChangedAt: new Date().toISOString(),
          statusChangedBy: store.getState().auth.agiliteUser?._id
        }

        // Add to status history
        if (!currentClaim.statusHistory) {
          currentClaim.statusHistory = []
        }

        currentClaim.statusHistory.push({
          status: newStatus,
          previousStatus: currentClaim.status,
          changedAt: new Date().toISOString(),
          changedBy: store.getState().auth.agiliteUser?._id,
          notes
        })

        updateData.statusHistory = currentClaim.statusHistory

        // Set date submitted if transitioning to submitted
        if (newStatus === BillingEnums.claimStatus.SUBMITTED) {
          updateData.dateSubmitted = new Date().toISOString()
        }

        const response = await updateCompensationClaim(claimId, updateData)
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Add document to claim
 * @param {string} claimId - Claim ID
 * @param {Object} document - Document data
 * @returns {Promise} Updated claim
 */
export const addClaimDocument = (claimId, document) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get current claim
        const claim = await getCompensationClaimById(claimId)

        if (!claim) {
          throw new Error(BillingEnums.errorMessages.CLAIM_NOT_FOUND)
        }

        // Add document to array
        if (!claim.documents) {
          claim.documents = []
        }

        claim.documents.push({
          ...document,
          uploadDate: new Date().toISOString(),
          uploadedBy: store.getState().auth.agiliteUser?._id
        })

        const response = await updateCompensationClaim(claimId, { documents: claim.documents })
        resolve(response)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Delete compensation claim
 * @param {string} claimId - Claim ID
 * @returns {Promise} Deletion result
 */
export const deleteCompensationClaim = (claimId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.DELETE,
          {
            recordId: claimId
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

/**
 * Get claims statistics by type
 * @returns {Promise} Statistics data
 */
export const getClaimsStatsByType = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const pipeline = [
          {
            $match: {
              entityRef: store.getState().core.entity._id
            }
          },
          {
            $group: {
              _id: {
                type: '$claimType',
                status: '$status'
              },
              count: { $sum: 1 },
              totalAmount: { $sum: '$amount' }
            }
          },
          {
            $group: {
              _id: '$_id.type',
              statusBreakdown: {
                $push: {
                  status: '$_id.status',
                  count: '$count',
                  totalAmount: '$totalAmount'
                }
              },
              totalClaims: { $sum: '$count' },
              totalAmount: { $sum: '$totalAmount' }
            }
          }
        ]

        const response = await agilite.Connectors.execute(
          BillingEnums.profileKeys.COMPENSATION_CLAIMS,
          BillingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
